const HUMANIZE_ACRONYMS = new Set(['AI', 'API', 'PII', 'ID', 'URL']);

export const NUMBER_LOCALE = 'en-US';

export function pluralize(count, singular, plural = `${singular}s`) {
    return Number(count) === 1 ? singular : plural;
}

/** Numbers from here on show compact: 1.2K, 3.4M, 1.1B (every metric on every page, decided
 *  2026-10-17; the metrics engine's values follow the same rule in src/metrics/format.js). */
export const COMPACT_FROM = 1000;

/**
 * Format a number for display: compact from 1,000 (1.2K, 3.4M), grouped below it.
 * `{ exact: true }` keeps the full grouped figure (a hover, an export, a raw value).
 * Pinning the locale prevents browsers in Indian locales from rendering
 * western millions as 12,34,567 instead of 1,234,567.
 */
export function formatNumberWithCommas(num, options = {}) {
    // Missing or unmeasured is "—", never a real-looking 0 (audit row C22).
    // A real 0 still prints "0".
    if (num === null || num === undefined || num === '') {
        return '—';
    }

    const parsedNum = Number(num);
    if (!Number.isFinite(parsedNum)) {
        return '—';
    }

    const { exact = false, ...format } = options;
    if (!exact && Math.abs(parsedNum) >= COMPACT_FROM) {
        return new Intl.NumberFormat(NUMBER_LOCALE, {
            notation: 'compact', compactDisplay: 'short', maximumFractionDigits: 1,
        }).format(parsedNum);
    }
    return new Intl.NumberFormat(NUMBER_LOCALE, format).format(parsedNum);
}

/** Format a grouped number and its correctly pluralized label. */
export function formatCount(count, singular, plural = `${singular}s`, options = {}) {
    const numericCount = Number(count);
    const safeCount = Number.isFinite(numericCount) ? numericCount : 0;
    return `${formatNumberWithCommas(safeCount, options)} ${pluralize(safeCount, singular, plural)}`;
}

/** Format a percentage with a shared precision and invalid-value fallback. */
export function formatPercentage(value, options = {}) {
    const { fallback = 'N/A', maximumFractionDigits = 1, ...numberOptions } = options;
    if (value === null || value === undefined || value === '') return fallback;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return fallback;
    return `${formatNumberWithCommas(numericValue, {
        exact: true, // a percentage is never shortened
        maximumFractionDigits,
        ...numberOptions,
    })}%`;
}

const DURATION_UNITS = {
    second: { short: 'sec', singular: 'second', plural: 'seconds' },
    minute: { short: 'min', singular: 'minute', plural: 'minutes' },
    hour: { short: 'hr', singular: 'hour', plural: 'hours' },
    day: { short: 'day', singular: 'day', plural: 'days' },
};

/** Format a duration with the same unit labels and number grouping everywhere. */
export function formatDuration(value, unit = 'minute', options = {}) {
    const numericValue = Number(value);
    const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
    const normalizedUnit = String(unit).toLowerCase().replace(/s$/, '');
    const unitConfig = DURATION_UNITS[normalizedUnit] || {
        short: normalizedUnit,
        singular: normalizedUnit,
        plural: `${normalizedUnit}s`,
    };
    const { style = 'short', ...numberOptions } = options;
    const formattedValue = formatNumberWithCommas(safeValue, numberOptions);
    const label = style === 'long'
        ? pluralize(safeValue, unitConfig.singular, unitConfig.plural)
        : unitConfig.short;
    return `${formattedValue} ${label}`;
}

/**
 * Turn snake_case / camelCase API tokens into readable UI labels.
 * Preserves well-known acronyms (AI, API, PII, ID, URL). Null-safe.
 */
export function humanizeLabel(value) {
    if (value === null || value === undefined) return '';
    const raw = String(value).trim();
    if (!raw) return '';

    const spaced = raw
        .replace(/_/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');

    return spaced
        .split(/\s+/)
        .map((word) => {
            const upper = word.toUpperCase();
            if (HUMANIZE_ACRONYMS.has(upper)) return upper;
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
}

/**
 * Friendly one-liner for raw backend error strings shown in UI error boxes.
 * Bare snake_case/camelCase identifiers (e.g. openAI_alert_logs_failed)
 * become readable labels; full sentences and codes pass through truncated.
 * Null-safe.
 */
export function friendlyErrorText(value, maxLen = 180) {
    if (value === null || value === undefined) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    const looksLikeIdentifier = /_/.test(raw) || /[a-z0-9][A-Z]/.test(raw);
    if (!/\s/.test(raw) && looksLikeIdentifier) return humanizeLabel(raw).slice(0, maxLen);
    return raw.slice(0, maxLen);
}

/**
 * Weighted success rate: sum(successes) / sum(stateful interactions).
 * Falls back to total_interactions when stateful_interactions is absent.
 */
export function computeWeightedSuccessRate(agents = []) {
    if (!agents.length) return 0;

    let totalWeight = 0;
    let totalSuccesses = 0;

    agents.forEach((agent) => {
        const weight = Number(agent.stateful_interactions ?? agent.total_interactions) || 0;
        const rate = Number(agent.success_rate) || 0;
        totalWeight += weight;
        totalSuccesses += weight * (rate / 100);
    });

    if (totalWeight === 0) return 0;
    return Math.round((totalSuccesses / totalWeight) * 1000) / 10;
}

export function formatLargeNumber(num, decimals = 1) {
    const precision = Number.isInteger(decimals) && decimals >= 0 ? decimals : 1;
    return formatNumberWithCommas(num, { maximumFractionDigits: precision });
}

const LEGACY_MONTHS = {
    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
};
const LEGACY_UTC_TIMESTAMP = /^([A-Z][a-z]{2}) (\d{1,2}), (\d{4}) (\d{2}):(\d{2}):(\d{2}) UTC$/;

/**
 * Parse API / MySQL timestamps for relative-time display.
 * Bare ``2026-07-01T14:03:58`` (no offset) is UTC from the server;
 * ``new Date()`` alone treats that as local time and skews ~5.5h for IST.
 */
export function parseApiTimestamp(iso) {
    if (iso instanceof Date) return iso.getTime();
    if (typeof iso === 'number' && Number.isFinite(iso)) {
        return Math.abs(iso) < 1e11 ? iso * 1000 : iso;
    }
    if (!iso) return NaN;
    const s = String(iso).trim();
    if (!s) return NaN;
    if (/^-?\d+(?:\.\d+)?$/.test(s)) {
        const numeric = Number(s);
        return Math.abs(numeric) < 1e11 ? numeric * 1000 : numeric;
    }
    // Older backends pre-formatted the value ("Oct 20, 2026 05:19:27 UTC").
    // Payloads captured before they returned ISO must still render, not show N/A.
    const legacy = s.match(LEGACY_UTC_TIMESTAMP);
    if (legacy) {
        const month = LEGACY_MONTHS[legacy[1]];
        if (month !== undefined) {
            return Date.UTC(
                Number(legacy[3]), month, Number(legacy[2]),
                Number(legacy[4]), Number(legacy[5]), Number(legacy[6]),
            );
        }
    }
    if (/[Zz]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) {
        return new Date(s).getTime();
    }
    const normalized = s.includes('T') ? s : s.replace(' ', 'T');
    return new Date(`${normalized}Z`).getTime();
}

/** Format a compact calendar date for charts and summaries. */
export function formatShortDate(value, options = {}) {
    const { fallback = 'N/A', ...dateOptions } = options;
    let date;

    // Date-only API values represent calendar days, not UTC instants. Parsing
    // them as local midnight prevents the label shifting by a day in the UI.
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
        const [year, month, day] = value.trim().split('-').map(Number);
        date = new Date(year, month - 1, day);
        if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
            return fallback;
        }
    } else {
        const timestamp = parseApiTimestamp(value);
        if (Number.isNaN(timestamp)) return fallback;
        date = new Date(timestamp);
    }

    if (Number.isNaN(date.getTime())) return fallback;
    return date.toLocaleDateString(NUMBER_LOCALE, {
        month: 'short',
        day: 'numeric',
        ...dateOptions,
    });
}

/**
 * Consistent relative time for activity lists. Older values can fall back to
 * either a short date or the full timezone-aware timestamp.
 */
export function formatRelativeTime(iso, options = {}) {
    const {
        fallback = 'Never',
        absoluteAfterDays = null,
        absoluteFormat = 'timestamp',
        absoluteOptions = {},
    } = options;
    const then = parseApiTimestamp(iso);
    if (Number.isNaN(then)) return fallback;

    const diffSeconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
    const minutes = Math.floor(diffSeconds / 60);
    const hours = Math.floor(diffSeconds / 3600);
    const days = Math.floor(diffSeconds / 86400);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${formatDuration(minutes, 'minute')} ago`;
    if (hours < 24) return `${formatDuration(hours, 'hour')} ago`;
    if (Number.isFinite(absoluteAfterDays) && days >= absoluteAfterDays) {
        return absoluteFormat === 'date'
            ? formatShortDate(iso, absoluteOptions)
            : formatTimestampWithTimezone(iso, absoluteOptions);
    }
    return `${formatDuration(days, 'day', { style: 'long' })} ago`;
}

/** Short relative label: "just now" / "5m ago" / "2h ago" / "3d ago". */
export function formatRelativeAgo(iso) {
    const then = parseApiTimestamp(iso);
    if (Number.isNaN(then)) return 'never';
    const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} h ago`;
    return `${Math.floor(diffSec / 86400)} d ago`;
}

/**
 * Relative activity timestamp used by agent inventory/detail surfaces.
 * Missing and malformed values represent an agent with no activity in the
 * selected period and must never leak JavaScript's "Invalid Date" label.
 */
export function formatActivityTime(iso) {
    const then = parseApiTimestamp(iso);
    if (Number.isNaN(then)) return 'Never';

    const diffMs = Math.max(0, Date.now() - then);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return new Date(then).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format an API timestamp in the viewer's local timezone and name that zone.
 * Bare server timestamps are interpreted as UTC via parseApiTimestamp.
 */
export function formatTimestampWithTimezone(iso, options = {}) {
    const timestamp = parseApiTimestamp(iso);
    if (Number.isNaN(timestamp)) return 'N/A';

    const { includeSeconds = false, includeWeekday = false, ...overrides } = options;
    return new Date(timestamp).toLocaleString('en-US', {
        ...(includeWeekday ? { weekday: 'short' } : {}),
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        ...(includeSeconds ? { second: '2-digit' } : {}),
        timeZoneName: 'short',
        ...overrides,
    });
}

/** Format only the clock portion of an API timestamp in the viewer's local zone. */
export function formatTimeWithTimezone(iso, options = {}) {
    const timestamp = parseApiTimestamp(iso);
    if (Number.isNaN(timestamp)) return 'N/A';

    const { includeSeconds = true, ...overrides } = options;
    return new Date(timestamp).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        ...(includeSeconds ? { second: '2-digit' } : {}),
        timeZoneName: 'short',
        ...overrides,
    });
}

/**
 * Format an API timestamp for exported reports (CSV/JSON) as an unambiguous, timezone-
 * explicit ISO-8601 string in the viewer's local zone, e.g. ``2026-07-18T16:20:00+05:30``.
 * This matches the wall-clock time shown on screen (via formatTimestampWithTimezone) while
 * carrying an explicit offset, and — unlike the localized display form — contains no commas,
 * so it is safe in comma-joined CSV rows. Returns '' for missing/invalid input.
 */
export function formatTimestampForExport(iso) {
    const ts = parseApiTimestamp(iso);
    if (Number.isNaN(ts)) return '';
    const d = new Date(ts);
    const pad = (n) => String(n).padStart(2, '0');
    const offMin = -d.getTimezoneOffset(); // minutes east of UTC
    const sign = offMin >= 0 ? '+' : '-';
    const abs = Math.abs(offMin);
    const offset = `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
        + `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}${offset}`;
}

/**
 * Converts a date string to a human-readable relative time format.
 * Examples: "Generated just now", "Generated 5 minutes ago", "Generated 2 hours 15 minutes ago", "Generated 3 days 5 hours ago"
 * @param {string} dateString ISO date string or any valid date string
 * @returns {string} Relative time string with "Generated" prefix
 */
export function getRelativeTimeString(dateString) {
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now.getTime() - past.getTime();
    
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffSec < 60) {
        return 'Generated just now';
    } else if (diffMin < 60) {
        return `Generated ${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
    } else if (diffHour < 24) {
        const remainingMin = diffMin % 60;
        if (remainingMin > 0) {
            return `Generated ${diffHour} hour${diffHour !== 1 ? 's' : ''} ${remainingMin} minute${remainingMin !== 1 ? 's' : ''} ago`;
        }
        return `Generated ${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
    } else {
        const remainingHour = diffHour % 24;
        if (remainingHour > 0) {
            return `Generated ${diffDay} day${diffDay !== 1 ? 's' : ''} ${remainingHour} hour${remainingHour !== 1 ? 's' : ''} ago`;
        }
        return `Generated ${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
    }
}

/**
 * Copies text to the clipboard and provides user feedback.
 * @param {string} text The text to copy
 * @returns {Promise} Promise that resolves when copy is complete
 */
export async function copyToClipboard(text) {
    // Use navigator.clipboard for modern browsers
    if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error('Failed to copy text (Clipboard API): ', err);
            // Fallback for older browsers or restrictive environments
            fallbackCopy(text);
        }
    } else {
        fallbackCopy(text);
    }
}

// Fallback copy function for older browsers
function fallbackCopy(text) {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
        document.execCommand('copy');
    } catch (err) {
        console.error('Failed to copy text (execCommand): ', err);
        alert('Failed to copy text. Your browser may not support this feature.');
    } finally {
        document.body.removeChild(textArea);
    }
}
